import {
  AddIcon,
  MinusIcon,
  ButtonGroup,
  ButtonGroupItem,
  Tooltip,
  createEventData,
  pushToDataLayer,
  theme,
  DeprecatedArrowCloseIcon,
  DeprecatedArrowFullscreenIcon,
  DeprecatedArrowWidthIcon,
} from '@arnold/common';
import styled from '@emotion/styled';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { AreaExtensions } from 'rete-area-plugin';
import { CustomNodeEditor } from '../rete';

type ZoomControlsProps = {
  editor: CustomNodeEditor;
  userId?: string;
  fullscreen: boolean;
  setFullscreen: (fullsreen: boolean) => void;
  showAiEditor?: boolean;
  aiSidebarOpen?: boolean;
  onToggleAiEditor?: () => void;
};

/** Above .arielFullscreen (1000) and Ariel AI sidebar (1001) so controls stay clickable. */
const ZOOM_CONTROLS_Z_INDEX = 1002;

const Container = styled.div`
  position: absolute;
  top: ${theme.spacing.f};
  right: ${theme.spacing.f};
  z-index: ${ZOOM_CONTROLS_Z_INDEX};
`;

const AiToolbarLabel = styled.span`
  color: ${theme.colors.ai.default};
  font-weight: 600;
`;

export const ZoomControls: FC<ZoomControlsProps> = ({
  editor,
  userId,
  fullscreen,
  setFullscreen,
  showAiEditor,
  aiSidebarOpen,
  onToggleAiEditor,
}) => {
  const { t } = useTranslation(['arielZoomControls', 'topicEditor']);

  const buttonGroupItems: ButtonGroupItem[] = [
    {
      item: (
        <Tooltip title={t('zoomOut')}>
          <MinusIcon />
        </Tooltip>
      ),
      color: 'highlighted',
      dataIcom: 'ariel-zoom-out',
      onClick: () => {
        if (userId) {
          pushToDataLayer({
            userId,
            event: 'ux.user-ariel-zoom',
            ...createEventData('ariel', 'zoomButton', 'user clicked on zoom buttons'),
          });
        }
        const positionInfo = editor.areaPlugin.container.firstElementChild?.getBoundingClientRect();
        const containerPositionInfo = editor.areaPlugin.container?.getBoundingClientRect();
        if (!positionInfo) {
          return;
        }
        // Replicate calculation from rete-area-plugin.
        const ox =
          (positionInfo.left - containerPositionInfo.left - (window.innerWidth - containerPositionInfo.left) / 2) *
          -0.2;
        const oy =
          (positionInfo.top - containerPositionInfo.top - (window.innerHeight - containerPositionInfo.top) / 2) * -0.2;
        editor.areaPlugin.area.zoom(editor.areaPlugin.area.transform.k * 0.8, ox, oy);
      },
    },
    {
      item: (
        <Tooltip title={t('zoomIn')}>
          <AddIcon />
        </Tooltip>
      ),
      color: 'highlighted',
      dataIcom: 'ariel-zoom-in',
      onClick: () => {
        if (userId) {
          pushToDataLayer({
            userId,
            event: 'ux.user-ariel-zoom',
            ...createEventData('ariel', 'zoomButton', 'user clicked on zoom buttons'),
          });
        }

        const positionInfo = editor.areaPlugin.container.firstElementChild?.getBoundingClientRect();
        const containerPositionInfo = editor.areaPlugin.container?.getBoundingClientRect();
        if (!positionInfo) {
          return;
        }
        // Replicate calculation from rete-area-plugin.
        const ox =
          (positionInfo.left - containerPositionInfo.left - (window.innerWidth - containerPositionInfo.left) / 2) *
          0.25;
        const oy =
          (positionInfo.top - containerPositionInfo.top - (window.innerHeight - containerPositionInfo.top) / 2) * 0.25;
        editor.areaPlugin.area.zoom(editor.areaPlugin.area.transform.k * 1.25, ox, oy);
      },
    },
    {
      item: (
        <Tooltip title={t('pinToScreen')}>
          <DeprecatedArrowWidthIcon />
        </Tooltip>
      ),
      color: 'highlighted',
      dataIcom: 'ariel-pin-to-screen',
      onClick: () => {
        if (userId) {
          pushToDataLayer({
            userId,
            event: 'ux.user-ariel-pin-to-screen',
            ...createEventData('ariel', 'pinToScreenButton', 'user clicked on pin to screen button'),
          });
        }
        AreaExtensions.zoomAt(editor.areaPlugin, editor.getNodes());
      },
    },
    fullscreen
      ? {
          item: (
            <Tooltip key="fullscreen" title={t('exitFullscreen')}>
              <DeprecatedArrowCloseIcon />
            </Tooltip>
          ),
          color: 'highlighted',
          dataIcom: 'ariel-exit-fullscreen',
          onClick: () => {
            if (userId) {
              pushToDataLayer({
                userId,
                event: 'ux.user-ariel-exit-fullscreen',
                ...createEventData('ariel', 'exitFullscreenButton', 'user clicked on exit fullscreen button'),
              });
            }
            setFullscreen(false);
          },
        }
      : {
          item: (
            <Tooltip key="fullscreen" title={t('fullscreen')}>
              <DeprecatedArrowFullscreenIcon />
            </Tooltip>
          ),
          color: 'highlighted',
          dataIcom: 'ariel-fullscreen',
          onClick: () => {
            if (userId) {
              pushToDataLayer({
                userId,
                event: 'ux.user-ariel-fullscreen',
                ...createEventData('ariel', 'fullscreenButton', 'user clicked on fullscreen button'),
              });
            }
            setFullscreen(true);
          },
        },
  ];

  if (showAiEditor) {
    buttonGroupItems.push({
      item: (
        <Tooltip title={aiSidebarOpen ? t('topicEditor:arielAiClose') : t('topicEditor:arielAiOpen')}>
          <AiToolbarLabel>AI</AiToolbarLabel>
        </Tooltip>
      ),
      color: 'default',
      dataIcom: 'btn-ariel-ai-editor',
      onClick: () => onToggleAiEditor?.(),
    });
  }

  return (
    <Container>
      <ButtonGroup buttonGroupItems={buttonGroupItems} />
    </Container>
  );
};
